import React, { useEffect, useState, useMemo } from "react";
import styled from "styled-components";
import { useSelector, useDispatch, shallowEqual } from "react-redux";

// Data
import { useSurveyDataContext } from "../Data/SurveyViewContext";
import { GET_EMPLOYEES } from '../../../../../constants/actions'

// Components
import Modal from "new_components/molecules/Modal";
import Button from "new_components/atoms/Button";
import TextEditor from "new_components/organisms/TextEditor";
import TextInput from "new_components/molecules/TextInput";
import SectionHeader from "./SectionHeader";

// Constants
import colors from "styles/colors";
import { ICON_SET } from "new_components/atoms/Icon/IconSet";
import ImageButton from "new_components/molecules/ImageButton";

// Icons
const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
  </svg>
)

const UsersIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
  </svg>
)

const PendingAssigneesRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  margin-bottom: 20px;
`;

const AssigneeChip = styled.div`
  background-color: ${colors.background.lightBlue};
  color: ${colors.text.dark};
  padding: 8px 12px;
  border-radius: 16px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const AddAssigneesModal = ({
  isOpen,
  onClose,
  onConfirm,
  errors,
  pendingUsers,
  removePendingUser,
  handleEmailUpdate,
}) => {
  const dispatch = useDispatch();
  const { fetched_scheduled_task, get_employee_org_category, get_survey_structure } = useSurveyDataContext();
  
  // Email state
  const [emailSubject, setEmailSubject] = useState(
    fetched_scheduled_task?.task_template_email?.email_subject || ""
  );
  const [emailBody, setEmailBody] = useState(
    fetched_scheduled_task?.task_template_email?.email_content || ""
  );

  // Employee selection state (matching ScheduleSurvey pattern)
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [activeTab, setActiveTab] = useState('individual'); // "individual" or "category"
  const [expandedCategory, setExpandedCategory] = useState(null);

  // Redux state for employees
  const { employees } = useSelector(
    (state) => ({
      employees: Array.isArray(state.employees?.employees) ? state.employees.employees : [],
    }),
    shallowEqual,
  );

  // Load employees if needed
  useEffect(() => {
    if (!employees?.length) {
      dispatch({ type: GET_EMPLOYEES });
    }
  }, [dispatch, employees?.length]);

  useEffect(() => {
    if (
      emailSubject &&
      emailBody &&
      fetched_scheduled_task &&
      handleEmailUpdate
    ) {
      handleEmailUpdate(emailSubject, emailBody);
    }
  }, [emailSubject, emailBody, fetched_scheduled_task, handleEmailUpdate]);

  useEffect(() => {
    setEmailSubject(
      fetched_scheduled_task?.task_template_email?.email_subject || ""
    );
    setEmailBody(
      fetched_scheduled_task?.task_template_email?.email_content || ""
    );
  }, [fetched_scheduled_task]);

  // Build accordion categories (from ScheduleSurvey pattern)
  const accordionCategories = useMemo(() => {
    if (!get_survey_structure?.[0]?.categories) return [];

    return get_survey_structure[0].categories
      .filter((cat) => cat.visibility !== 'private' && !cat.demographic && cat.priority !== 'primary' && cat.priority !== 'secondary')
      .map((cat) => ({
        id: cat.id,
        name: cat.name,
        priority: cat.priority,
        type: 'employee-filtering',
        options: cat.options.map((opt) => ({
          id: opt.id,
          name: opt.name,
          text: opt.text || opt.name,
        })),
      }));
  }, [get_survey_structure]);

  // Filter employees based on search term and selected categories
  const filteredEmployees = useMemo(() => {
    if (!Array.isArray(employees)) return [];

    let filtered = employees.filter((emp) => emp.status === 1); // Only active employees

    if (selectedCategories.length > 0) {
      filtered = filtered.filter((emp) => {
        const empCategories = get_employee_org_category?.find(
          (empCat) => empCat.employee === emp.id,
        );

        if (!empCategories?.category) return false;

        return selectedCategories.some((selectedCatId) => {
          const selectedKey = String(selectedCatId);
          return empCategories.category.some((cat) => {
            if (!cat) return false;
            return (
              String(cat.value_id) === selectedKey ||
              String(cat.value) === selectedKey
            );
          });
        });
      });
    } else if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (emp) =>
          emp.first_name?.toLowerCase().includes(lowerSearch) ||
          emp.last_name?.toLowerCase().includes(lowerSearch) ||
          emp.email?.toLowerCase().includes(lowerSearch),
      );
    }

    return filtered;
  }, [employees, searchTerm, selectedCategories, get_employee_org_category]);

  const addableEmployees = useMemo(
    () =>
      filteredEmployees.filter((emp) => !selectedEmployees.includes(emp.id)),
    [filteredEmployees, selectedEmployees],
  );

  // Handle employee selection
  const handleEmployeeToggle = (employeeId) => {
    setSelectedEmployees((prev) =>
      prev.includes(employeeId) ? prev.filter((id) => id !== employeeId) : [...prev, employeeId],
    );
  };

  // Handle category selection
  const handleCategoryToggle = (categoryOptionId) => {
    setSelectedCategories((prev) =>
      prev.includes(categoryOptionId)
        ? prev.filter((id) => id !== categoryOptionId)
        : [...prev, categoryOptionId],
    );
  };

  // Toggle category expand
  const toggleCategoryExpand = (categoryId) => {
    setExpandedCategory((prev) => (prev === categoryId ? null : categoryId));
  };

  // Add employees from category selection
  const addCategoryEmployees = () => {
    const employeeIds = addableEmployees.map((emp) => emp.id);

    if (employeeIds.length === 0) {
      setSelectedCategories([]);
      setExpandedCategory(null);
      return;
    }

    setSelectedEmployees((prev) => [...new Set([...prev, ...employeeIds])]);
    setSelectedCategories([]);
    setExpandedCategory(null);
  };

  // Clear all selected employees
  const clearAllEmployees = () => {
    setSelectedEmployees([]);
    setSelectedCategories([]);
    setExpandedCategory(null);
  };

  const pendingRow = () => {
    return (
      <PendingAssigneesRow>
        {pendingUsers.map((user) => (
          <AssigneeChip key={user.value}>
            <ImageButton
              icon={ICON_SET.close}
              text={user.text}
              color={colors.text.dark}
              textStyle={true}
              iconPosition="right"
              onClick={() => removePendingUser(user.value)}
            />
          </AssigneeChip>
        ))}
      </PendingAssigneesRow>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      modalHeading="SEND SURVEY TO NEW PARTICIPANTS"
      modalWidth="1000px"
      modalBody={
        <ModalBodyContainer>
          {/* Pending Users Section */}
          {pendingUsers.length > 0 && pendingRow()}
          
          {/* Employee Selection Section */}
          <PanelSection>
            <SectionTitle>
              <UsersIcon />
              <span>Select Recipients ({selectedEmployees.length} selected)</span>
            </SectionTitle>

            {/* Tab Navigation */}
            <TabContainer>
              <Tab
                type="button"
                active={activeTab === 'individual'}
                onClick={() => {
                  setActiveTab('individual');
                  setSearchTerm('');
                  setSelectedCategories([]);
                }}
              >
                Individual Selection
              </Tab>
              <Tab
                type="button"
                active={activeTab === 'category'}
                onClick={() => {
                  setActiveTab('category');
                  setSearchTerm('');
                }}
              >
                By Category
              </Tab>
            </TabContainer>

            <EmployeeSelectionLayout>
              {/* Left: Selection Interface */}
              <SelectionPanel>
                {/* Individual Selection Tab */}
                {activeTab === 'individual' && (
                  <SelectionContent>
                    <SearchContainer>
                      <SearchInputWrapper>
                        <SearchIconWrapper>
                          <SearchIcon />
                        </SearchIconWrapper>
                        <SearchInput
                          type="text"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          placeholder="Search contacts..."
                        />
                      </SearchInputWrapper>
                      <SearchHint>
                        {searchTerm
                          ? `${filteredEmployees.length} contact${
                              filteredEmployees.length !== 1 ? 's' : ''
                            } found`
                          : `${employees.length} total contacts available`}
                      </SearchHint>
                    </SearchContainer>

                    {searchTerm ? (
                      <EmployeesList>
                        {filteredEmployees.map((emp) => (
                          <EmployeeItem
                            key={emp.id}
                            selected={selectedEmployees.includes(emp.id)}
                            onClick={() => handleEmployeeToggle(emp.id)}
                          >
                            <EmployeeCheckbox
                              type="checkbox"
                              checked={selectedEmployees.includes(emp.id)}
                              onChange={() => handleEmployeeToggle(emp.id)}
                            />
                            <EmployeeDetails>
                              <EmployeeName>
                                {emp.first_name} {emp.last_name}
                              </EmployeeName>
                              <EmployeeEmail>{emp.email}</EmployeeEmail>
                            </EmployeeDetails>
                          </EmployeeItem>
                        ))}
                        {filteredEmployees.length === 0 && (
                          <EmptyState>
                            <EmptyTitle>No contacts found</EmptyTitle>
                            <EmptyText>Try adjusting your search terms</EmptyText>
                          </EmptyState>
                        )}
                      </EmployeesList>
                    ) : (
                      <SearchPrompt>
                        <SearchPromptIcon>👥</SearchPromptIcon>
                        <SearchPromptTitle>Search for contacts</SearchPromptTitle>
                        <SearchPromptText>
                          Start typing to find contacts by name or email
                        </SearchPromptText>
                      </SearchPrompt>
                    )}
                  </SelectionContent>
                )}

                {/* Category Selection Tab */}
                {activeTab === 'category' && (
                  <SelectionContent>
                    {(selectedCategories.length > 0) && (
                      <CategoryAddSection>
                        <CategoryAddInfo>
                          {addableEmployees.length > 0
                            ? `${addableEmployees.length} new contact${
                                addableEmployees.length !== 1 ? 's' : ''
                              } ready to add${
                                filteredEmployees.length > addableEmployees.length
                                  ? ` (${filteredEmployees.length - addableEmployees.length} already selected)`
                                  : ''
                              }`
                            : filteredEmployees.length > 0
                            ? 'Everyone matching this selection is already selected'
                            : 'No contacts match this selection'}
                        </CategoryAddInfo>
                        <CategoryAddButton
                          type="button"
                          onClick={addCategoryEmployees}
                          disabled={addableEmployees.length === 0}
                        >
                          Add {addableEmployees.length} Contact
                          {addableEmployees.length !== 1 ? 's' : ''}
                        </CategoryAddButton>
                      </CategoryAddSection>
                    )}

                    {accordionCategories.length > 0 ? (
                      <CategoryFilters>
                        {accordionCategories.map((category) => {
                          const selectedCount = category.options.filter((opt) =>
                            selectedCategories.includes(opt.id)
                          ).length;

                          return (
                            <CategoryGroup key={category.id}>
                              <CategoryGroupHeader
                                onClick={() => toggleCategoryExpand(category.id)}
                              >
                                <CategoryGroupLeft>
                                  <CategoryCollapseIcon
                                    expanded={expandedCategory === category.id}
                                  >
                                    {expandedCategory === category.id ? '▼' : '▶'}
                                  </CategoryCollapseIcon>
                                  <CategoryName>{category.name}</CategoryName>
                                </CategoryGroupLeft>
                                <CategoryCount>
                                  {selectedCount} of {category.options.length}
                                </CategoryCount>
                              </CategoryGroupHeader>

                              {expandedCategory === category.id && (
                                <CategoryOptions>
                                  {category.options.map((option) => {
                                    const isSelected = selectedCategories.includes(option.id);
                                    
                                    return (
                                      <CategoryOption
                                        key={option.id}
                                        selected={isSelected}
                                        onClick={() => handleCategoryToggle(option.id)}
                                      >
                                        <CategoryOptionCheckbox
                                          type="checkbox"
                                          checked={isSelected}
                                          readOnly
                                        />
                                        <CategoryOptionText>{option.name}</CategoryOptionText>
                                      </CategoryOption>
                                    );
                                  })}
                                </CategoryOptions>
                              )}
                            </CategoryGroup>
                          );
                        })}
                      </CategoryFilters>
                    ) : (
                      <EmptyState>
                        <EmptyTitle>No categories available</EmptyTitle>
                        <EmptyText>
                          Contact your administrator to set up employee categories
                        </EmptyText>
                      </EmptyState>
                    )}
                  </SelectionContent>
                )}
              </SelectionPanel>

              {/* Right: Selected Employees */}
              <SelectedPanel>
                <SelectedPanelHeader>
                  <SummaryTitle>
                    Selected Recipients ({selectedEmployees.length})
                  </SummaryTitle>
                  {selectedEmployees.length > 0 && (
                    <ClearButton onClick={clearAllEmployees}>Clear All</ClearButton>
                  )}
                </SelectedPanelHeader>

                {selectedEmployees.length > 0 ? (
                  <SelectedEmployeesFullList>
                    {selectedEmployees.map((empId) => {
                      const emp = employees.find((e) => e.id === empId);
                      return emp ? (
                        <SelectedEmployeeFullCard key={empId}>
                          <SelectedEmployeeDetails>
                            <SelectedEmployeeName>
                              {emp.first_name} {emp.last_name}
                            </SelectedEmployeeName>
                            <SelectedEmployeeEmail>{emp.email}</SelectedEmployeeEmail>
                            {emp.role_name && (
                              <SelectedEmployeeRole>{emp.role_name}</SelectedEmployeeRole>
                            )}
                          </SelectedEmployeeDetails>
                          <RemoveButton onClick={() => handleEmployeeToggle(empId)}>
                            ×
                          </RemoveButton>
                        </SelectedEmployeeFullCard>
                      ) : null;
                    })}
                  </SelectedEmployeesFullList>
                ) : (
                  <EmptySelectedState>
                    <EmptySelectedIcon>👥</EmptySelectedIcon>
                    <EmptySelectedTitle>No recipients selected</EmptySelectedTitle>
                    <EmptySelectedText>
                      Use the selection options on the left to choose survey recipients.
                    </EmptySelectedText>
                  </EmptySelectedState>
                )}
              </SelectedPanel>
            </EmployeeSelectionLayout>
          </PanelSection>

          {/* Email Configuration Section */}
          <PanelSection>
            <SectionTitle>
              <SectionHeader
                text="Set E-mail Subject and Body"
                icon={ICON_SET.mail}
              />
            </SectionTitle>
            
            <EmailFormGroup>
              <TextInput
                label={"Email Subject"}
                value={emailSubject}
                onChange={(e) => {
                  setEmailSubject(e.target.value);
                }}
                error={errors?.emailSubjectValidation}
              />
            </EmailFormGroup>
            
            <EmailFormGroup>
              <TextEditor
                hasError={errors?.emailBodyValidation}
                onChange={setEmailBody}
                initialValue={emailBody}
                value={emailBody}
              />
            </EmailFormGroup>
          </PanelSection>

          {/* Action Buttons */}
          <ModalFooter>
            <Button
              onClick={() => {
                onConfirm();
              }}
              disabled={selectedEmployees.length === 0}
            >
              Send to {selectedEmployees.length} Recipients
            </Button>
          </ModalFooter>
        </ModalBodyContainer>
      }
    />
  );
};

export default AddAssigneesModal;

// Styled Components (adapted from ScheduleSurvey.js)
const ModalBodyContainer = styled.div`
  padding: 0;
  max-height: 80vh;
  overflow-y: auto;
`;

const PanelSection = styled.div`
  margin-bottom: 24px;
  padding: 20px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: "Poppins";
  font-weight: 600;
  font-size: 16px;
  color: #414141;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
`;

const TabContainer = styled.div`
  display: flex;
  gap: 2px;
  background: #f0f0f0;
  padding: 3px;
  border-radius: 6px;
  margin-bottom: 16px;
  width: fit-content;
`;

const Tab = styled.button`
  padding: 8px 16px;
  background: ${props => props.active ? 'white' : 'transparent'};
  border: none;
  border-radius: 4px;
  color: ${props => props.active ? '#414141' : '#7e7e7e'};
  font-family: "Poppins";
  font-weight: 500;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: ${props => props.active ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'};
  white-space: nowrap;

  &:hover {
    background: ${props => props.active ? 'white' : '#e9ecef'};
  }
`;

const EmployeeSelectionLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-top: 16px;
  min-height: 400px;
`;

const SelectionPanel = styled.div`
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
`;

const SelectionContent = styled.div`
  padding: 16px;
  min-height: 300px;
`;

const SearchContainer = styled.div`
  margin-bottom: 16px;
`;

const SearchInputWrapper = styled.div`
  position: relative;
  margin-bottom: 6px;
`;

const SearchIconWrapper = styled.div`
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #7e7e7e;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 8px 8px 8px 36px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-family: "Poppins";
  font-size: 12px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #476dfa;
  }
`;

const SearchHint = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 11px;
  color: #7e7e7e;
  padding-left: 12px;
`;

const EmployeesList = styled.div`
  max-height: 250px;
  overflow-y: auto;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
`;

const EmployeeItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  cursor: pointer;
  transition: background-color 0.2s;
  border-bottom: 1px solid #f8f8f8;

  &:hover {
    background: #f8f9fa;
  }

  &:last-child {
    border-bottom: none;
  }

  ${props => props.selected && `
    background: #f0f4ff;
    border-color: #476dfa;
  `}
`;

const EmployeeCheckbox = styled.input`
  width: 14px;
  height: 14px;
`;

const EmployeeDetails = styled.div`
  flex: 1;
`;

const EmployeeName = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 12px;
  color: #414141;
  margin-bottom: 2px;
`;

const EmployeeEmail = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 11px;
  color: #7e7e7e;
`;

const SearchPrompt = styled.div`
  text-align: center;
  padding: 40px 20px;
`;

const SearchPromptIcon = styled.div`
  font-size: 32px;
  margin-bottom: 12px;
`;

const SearchPromptTitle = styled.div`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 14px;
  color: #414141;
  margin-bottom: 6px;
`;

const SearchPromptText = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #7e7e7e;
`;

const CategoryAddSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  background: #e8f5e8;
  border: 1px solid #c8e6c8;
  border-radius: 6px;
  margin-bottom: 16px;
`;

const CategoryAddInfo = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #2e7d2e;
`;

const CategoryAddButton = styled.button`
  padding: 6px 12px;
  background: #2e7d2e;
  border: none;
  border-radius: 4px;
  color: white;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 11px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #256925;
  }

  &:disabled {
    background: #a5d6a7;
    cursor: not-allowed;
    opacity: 0.85;
  }

  &:disabled:hover {
    background: #a5d6a7;
  }
`;

const CategoryFilters = styled.div`
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  overflow: hidden;
`;

const CategoryGroup = styled.div`
  border-bottom: 1px solid #f8f8f8;

  &:last-child {
    border-bottom: none;
  }
`;

const CategoryGroupHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  background: #fafafa;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #f0f0f0;
  }
`;

const CategoryGroupLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CategoryCollapseIcon = styled.span`
  font-size: 10px;
  color: #7e7e7e;
  transition: transform 0.2s;
`;

const CategoryName = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 12px;
  color: #414141;
`;

const CategoryCount = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 11px;
  color: #7e7e7e;
`;

const CategoryOptions = styled.div`
  padding: 8px 0;
  background: white;
`;

const CategoryOption = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px 6px 32px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #f8f9fa;
  }

  ${props => props.selected && `
    background: #f0f4ff;
  `}
`;

const CategoryOptionCheckbox = styled.input`
  width: 12px;
  height: 12px;
`;

const CategoryOptionText = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 11px;
  color: #414141;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 32px 16px;
`;

const EmptyTitle = styled.div`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 14px;
  color: #414141;
  margin-bottom: 4px;
`;

const EmptyText = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #7e7e7e;
`;

const SelectedPanel = styled.div`
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const SelectedPanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e0e0e0;
  background: white;
`;

const SummaryTitle = styled.div`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 13px;
  color: #414141;
`;

const ClearButton = styled.button`
  padding: 4px 8px;
  background: transparent;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  color: #7e7e7e;
  font-family: "Poppins";
  font-weight: 400;
  font-size: 11px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f8f9fa;
    border-color: #ff4444;
    color: #ff4444;
  }
`;

const SelectedEmployeesFullList = styled.div`
  flex: 1;
  padding: 16px;
  overflow-y: auto;
  max-height: 350px;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

const SelectedEmployeeFullCard = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  margin-bottom: 8px;
  transition: all 0.2s;

  &:hover {
    border-color: #476dfa;
    box-shadow: 0 2px 4px rgba(71, 109, 250, 0.1);
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const SelectedEmployeeDetails = styled.div`
  flex: 1;
`;

const SelectedEmployeeName = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  color: #414141;
  margin-bottom: 2px;
`;

const SelectedEmployeeEmail = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 11px;
  color: #7e7e7e;
  margin-bottom: 2px;
`;

const SelectedEmployeeRole = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 10px;
  color: #999;
  background: #f0f0f0;
  padding: 2px 6px;
  border-radius: 3px;
  display: inline-block;
  margin-top: 4px;
`;

const RemoveButton = styled.button`
  width: 20px;
  height: 20px;
  padding: 0;
  background: #ff4444;
  border: none;
  border-radius: 50%;
  color: white;
  font-size: 12px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #cc3333;
  }
`;

const EmptySelectedState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
`;

const EmptySelectedIcon = styled.div`
  font-size: 32px;
  margin-bottom: 12px;
  opacity: 0.5;
`;

const EmptySelectedTitle = styled.div`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 14px;
  color: #414141;
  margin-bottom: 6px;
`;

const EmptySelectedText = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #7e7e7e;
  max-width: 250px;
  line-height: 1.4;
`;

const EmailFormGroup = styled.div`
  margin-bottom: 16px;
`;

const ModalFooter = styled.div`
  padding: 20px;
  border-top: 1px solid #f0f0f0;
  background: #fafafa;
  display: flex;
  justify-content: flex-end;
`;
